/**
 * useAutoTemplateDialog — state + handlers for the auto-template create /
 * customize-copy dialog, extracted from BlogTemplatesPage.
 *
 * The merged initial-data builder: post-Phase-0 only the customize builder
 * survives (the edit builder was dead code); its behavior is kept verbatim —
 * system templates get a " (Copy)" name. (The page's toast for system
 * templates lived in the removed edit handler, not in customize.)
 *
 * CHANGE (Phase 1, plan of record): the create payload's default_config is
 * built via recipeToDefaultConfig (@/components/blog-templates/recipePayload)
 * — the single canonical serializer — instead of the hand-built flat+nested
 * construction. Consequences (all adjudicated):
 * - Canonical autoConfig key spellings (includesImages/includesList/
 *   imageArtStyle...), fixing the drift where create wrote only legacy names.
 * - FAQ keys are no longer written (R7: backend forces includesFAQ=True).
 * - enhanceFormatting is no longer written (R6; the form no longer carries
 *   the field, so the old payload always wrote `undefined` for it anyway).
 * - targetWordCount/tone/languageModel/imageModel stay top-level; link fields
 *   live only nested in autoConfig.
 *
 * Plan 1.6 (stop-sending dead fields, design-gate pre-merge condition):
 * - builder_blocks and template_type are no longer sent. The backend create
 *   route (template_crud.py create_template) never reads either field —
 *   builder_blocks is Optional[...] = None in the Pydantic schema and not
 *   forwarded to repo.create_template(); template_type is absent from the
 *   schema entirely. Both were already removed from the BlogTemplateCreate TS
 *   interface but survived in the inline payload object.
 * - content_template: '' is RETAINED as a disclosed deferral. The backend
 *   BlogTemplateCreate schema (template_builder.py:1492) declares it
 *   content_template: str (required, no default), so omitting it yields a 422.
 *   Making it nullable is Carl-gated Phase 5 step 5 (APPROACH-kimi.md:284-288:
 *   "make content_template nullable and stop writing it"), which itself
 *   requires Jeff's approval + the raw-SQL reader audit (:431-432). See the
 *   disclosed deferral in the PR #1008 body.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { blogTemplatesService, BlogTemplateSummary } from '@/services/blogTemplatesService';
import type { AutoTemplateFormData } from '@/components/blog-templates/AutoTemplateConfigForm';
import { formDataToRecipe, recipeToDefaultConfig } from '@/components/blog-templates/recipePayload';

export interface UseAutoTemplateDialogResult {
  showAutoTemplateDialog: boolean;
  setShowAutoTemplateDialog: (show: boolean) => void;
  isCreatingAutoTemplate: boolean;
  autoTemplateInitialData: Partial<AutoTemplateFormData> | undefined;
  /** Opens a fresh create dialog (clears any stale customize initialData —
   *  the old page reset initialData on dialog close; without this a plain
   *  Create after a Customize would reuse the copy's prefill). */
  handleOpenCreateDialog: () => void;
  /** Opens the dialog prefilled as a customized copy of the given template. */
  handleCustomizeTemplate: (template: BlogTemplateSummary) => void;
  /** Creates the template from the form config; closes the dialog on success. */
  handleCreateAutoTemplate: (config: AutoTemplateFormData) => Promise<void>;
}


export function useAutoTemplateDialog(): UseAutoTemplateDialogResult {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showAutoTemplateDialog, setShowAutoTemplateDialog] = useState(false);
  const [isCreatingAutoTemplate, setIsCreatingAutoTemplate] = useState(false);
  const [autoTemplateInitialData, setAutoTemplateInitialData] = useState<Partial<AutoTemplateFormData> | undefined>(undefined);

  const handleOpenCreateDialog = useCallback(() => {
    setAutoTemplateInitialData(undefined);
    setShowAutoTemplateDialog(true);
  }, []);

  const handleCustomizeTemplate = useCallback((template: BlogTemplateSummary) => {
    // All templates are now auto type - open the auto template config dialog with pre-filled data
    setAutoTemplateInitialData({
      name: `${template.name} (Copy)`,
      description: template.description || '',
      ...template.default_config?.autoConfig,
      targetWordCount: template.default_config?.targetWordCount || 1500,
      tone: template.default_config?.tone || 'professional',
      includeFAQ: template.default_config?.autoConfig?.includeFAQ ?? true,
      includeImages: template.default_config?.autoConfig?.includeImages ?? true,
      includeList: template.default_config?.autoConfig?.includeList ?? true,
      imageStyle: template.default_config?.autoConfig?.imageStyle || 'photorealistic',
      enhanceFormatting: template.default_config?.enhanceFormatting ?? false,
    });
    setShowAutoTemplateDialog(true);
  }, []);

  const handleCreateAutoTemplate = async (config: AutoTemplateFormData) => {
    setIsCreatingAutoTemplate(true);
    try {
      const slug = config.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      const templateData = {
        slug,
        name: config.name,
        description: config.description || '',
        // content_template: required by backend BlogTemplateCreate schema
        // (str, no default → 422 if omitted). Nullable is Carl-gated Phase 5
        // step 5 (APPROACH-kimi.md:284-288), gated on Jeff's approval + raw-SQL
        // reader audit. See disclosed deferral in PR #1008 body.
        content_template: '',
        default_config: recipeToDefaultConfig(formDataToRecipe(config)),
      };

      const newTemplate = await blogTemplatesService.create(templateData);
      toast({ title: 'Success', description: `Auto template "${newTemplate.name}" created` });
      setShowAutoTemplateDialog(false);
      // Refetch templates to show new one
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    } catch (err: unknown) {
      console.error('Failed to create auto template:', err);
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Failed to create auto template';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsCreatingAutoTemplate(false);
    }
  };

  return {
    showAutoTemplateDialog,
    setShowAutoTemplateDialog,
    isCreatingAutoTemplate,
    autoTemplateInitialData,
    handleOpenCreateDialog,
    handleCustomizeTemplate,
    handleCreateAutoTemplate,
  };
}
